import { LocaleDetails } from 'shared/src/types/languages.type';
import { getCurrencyForRegion } from 'shared/src/utils/currency';
import { budgetUnits } from 'shared/src/utils/budget-enforcement';

/**
 * Localized strings + formatting for the budget: the composer chip, the
 * shelf's scope note, the one sentence under a budgeted page's title, and the
 * note on the single pick worth going over for. Hand-translated en/fr/es/de,
 * English fallback for the other locales (same pattern as price-tier-labels.ts).
 *
 * Placeholders use {name} syntax (`fillBudgetCopy` below). This is product
 * copy: no em dashes.
 */

export interface BudgetUiContent {
  /** Composer chip before any number is set. */
  chipEmpty: string;
  /** Composer chip aria once a number is set — {budget} */
  chipAria: string;
  /** Composer chip aria before any number is set. */
  chipEmptyAria: string;
  /** The chip's inline amount field. */
  fieldAria: string;
  /** The number when it covers several units — {amount} {qty} ("$900 for 4") */
  budgetForQuantity: string;
  /** Label in front of the page sentence. */
  verdictLabel: string;
  /** Every priced pick fits — {budget} {top} {price} */
  verdictFitsAll: string;
  /** Every priced pick fits, some picks have no price yet — {budget} {top} {price} */
  verdictFitsAllPriced: string;
  /** Some picks fit — {count} {total} {budget} {top} {price} */
  verdictFitsSome: string;
  /** The one exception — {name} {price} {diff} */
  verdictException: string;
  /** The one exception, when it is the page's own first pick — {name} {price} {diff} */
  verdictExceptionTopPick: string;
  /** Nothing fits — {budget} {name} {price} {diff} */
  verdictNone: string;
  /** A page built for the number whose prices are not checked yet — {budget} */
  verdictUnpriced: string;
  /** Stretch note label. */
  stretchBadge: string;
  /** Stretch note amount — {diff} {budget} */
  stretchOverBy: string;
  /** Reason on a visitor-mode exception that is the page's own first pick. */
  stretchTopPickReason: string;
  /** Shelf scope note once the strip is filtered by the number — {budget} */
  shelfScope: string;
  /** Shelf line when no published pick fits — {budget} */
  shelfNothingFits: string;
  /** Shelf card total when the number covers several units — {total} {qty} */
  setTotal: string;
}

const CONTENT: Record<string, BudgetUiContent> = {
  en: {
    chipEmpty: 'Budget',
    chipAria: 'Budget {budget}. Edit',
    chipEmptyAria: 'Add a budget',
    fieldAria: 'Your maximum budget',
    budgetForQuantity: '{amount} for {qty}',
    verdictLabel: 'Your budget',
    verdictFitsAll: 'Every pick here fits {budget}. Our pick: {top}, at {price}.',
    verdictFitsAllPriced: 'Every priced pick here fits {budget}. Our pick: {top}, at {price}.',
    verdictFitsSome: '{count} of {total} picks fit {budget}. Our pick: {top}, at {price}.',
    verdictException: 'One is worth the stretch: {name}, at {price}, {diff} over.',
    verdictExceptionTopPick:
      'One is worth the stretch: {name}, our top pick on this page, at {price}, {diff} over.',
    verdictNone: "{budget} doesn't buy any of these. The closest is {name}, at {price}, {diff} over.",
    verdictUnpriced: 'Picked for your budget of {budget}.',
    stretchBadge: 'Worth the stretch',
    stretchOverBy: '{diff} over your {budget}',
    stretchTopPickReason: 'Our top pick on this page.',
    shelfScope: 'Under {budget}',
    shelfNothingFits:
      "Nothing we've published fits {budget} yet. Keep going and we'll build the page for your number.",
    setTotal: '{total} for {qty}',
  },
  fr: {
    chipEmpty: 'Budget',
    chipAria: 'Budget {budget}. Modifier',
    chipEmptyAria: 'Ajouter un budget',
    fieldAria: 'Votre budget maximum',
    budgetForQuantity: '{amount} pour {qty}',
    verdictLabel: 'Votre budget',
    verdictFitsAll: 'Tous les choix respectent votre budget de {budget}. Notre choix : {top}, à {price}.',
    verdictFitsAllPriced:
      'Tous les choix dont on connaît le prix respectent votre budget de {budget}. Notre choix : {top}, à {price}.',
    verdictFitsSome:
      '{count} choix sur {total} respectent votre budget de {budget}. Notre choix : {top}, à {price}.',
    verdictException: 'Un seul vaut le dépassement : {name}, à {price}, soit {diff} de plus.',
    verdictExceptionTopPick:
      'Un seul vaut le dépassement : {name}, notre premier choix sur cette page, à {price}, soit {diff} de plus.',
    verdictNone:
      'Aucun de ces choix ne respecte votre budget de {budget}. Le plus proche : {name}, à {price}, soit {diff} de plus.',
    verdictUnpriced: 'Choisis pour votre budget de {budget}.',
    stretchBadge: 'Dépassement assumé',
    stretchOverBy: '{diff} de plus que votre budget de {budget}',
    stretchTopPickReason: 'Notre premier choix sur cette page.',
    shelfScope: 'Sous {budget}',
    shelfNothingFits:
      "Rien de ce qu'on a publié ne respecte encore {budget}. Continuez, et on bâtira la page pour votre budget.",
    setTotal: '{total} pour {qty}',
  },
  es: {
    chipEmpty: 'Presupuesto',
    chipAria: 'Presupuesto {budget}. Editar',
    chipEmptyAria: 'Añadir un presupuesto',
    fieldAria: 'Tu presupuesto máximo',
    budgetForQuantity: '{amount} por {qty}',
    verdictLabel: 'Tu presupuesto',
    verdictFitsAll:
      'Todas las opciones entran en tu presupuesto de {budget}. Nuestra elección: {top}, a {price}.',
    verdictFitsAllPriced:
      'Todas las opciones con precio entran en tu presupuesto de {budget}. Nuestra elección: {top}, a {price}.',
    verdictFitsSome:
      '{count} de {total} opciones entran en tu presupuesto de {budget}. Nuestra elección: {top}, a {price}.',
    verdictException: 'Una vale la excepción: {name}, a {price}, {diff} más.',
    verdictExceptionTopPick:
      'Una vale la excepción: {name}, nuestra primera opción en esta página, a {price}, {diff} más.',
    verdictNone:
      'Ninguna de estas opciones entra en tu presupuesto de {budget}. La más cercana: {name}, a {price}, {diff} más.',
    verdictUnpriced: 'Elegidas para tu presupuesto de {budget}.',
    stretchBadge: 'Vale la pena',
    stretchOverBy: '{diff} más que tu presupuesto de {budget}',
    stretchTopPickReason: 'Nuestra primera opción en esta página.',
    shelfScope: 'Menos de {budget}',
    shelfNothingFits:
      'Nada de lo que hemos publicado entra todavía en {budget}. Sigue y crearemos la página para tu presupuesto.',
    setTotal: '{total} por {qty}',
  },
  de: {
    chipEmpty: 'Budget',
    chipAria: 'Budget {budget}. Bearbeiten',
    chipEmptyAria: 'Budget hinzufügen',
    fieldAria: 'Ihr Maximalbudget',
    budgetForQuantity: '{amount} für {qty}',
    verdictLabel: 'Ihr Budget',
    verdictFitsAll: 'Alle Empfehlungen passen in Ihr Budget von {budget}. Unsere Wahl: {top} für {price}.',
    verdictFitsAllPriced:
      'Alle Empfehlungen mit bekanntem Preis passen in Ihr Budget von {budget}. Unsere Wahl: {top} für {price}.',
    verdictFitsSome:
      '{count} von {total} Empfehlungen passen in Ihr Budget von {budget}. Unsere Wahl: {top} für {price}.',
    verdictException: 'Eine ist die Ausnahme wert: {name} für {price}, {diff} darüber.',
    verdictExceptionTopPick:
      'Eine ist die Ausnahme wert: {name}, unsere erste Wahl auf dieser Seite, für {price}, {diff} darüber.',
    verdictNone:
      'Keine dieser Empfehlungen passt in Ihr Budget von {budget}. Am nächsten kommt {name} für {price}, {diff} darüber.',
    verdictUnpriced: 'Ausgewählt für Ihr Budget von {budget}.',
    stretchBadge: 'Die Ausnahme wert',
    stretchOverBy: '{diff} über Ihrem Budget von {budget}',
    stretchTopPickReason: 'Unsere erste Wahl auf dieser Seite.',
    shelfScope: 'Unter {budget}',
    shelfNothingFits:
      'Noch passt nichts Veröffentlichtes in {budget}. Machen Sie weiter, dann erstellen wir die Seite für Ihr Budget.',
    setTotal: '{total} für {qty}',
  },
};

export const getBudgetUiContent = (languageCode: string): BudgetUiContent =>
  CONTENT[languageCode] ?? CONTENT.en;

/** Fill {name} placeholders; an unknown one is left as written. */
export const fillBudgetCopy = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match);

/**
 * Locale-aware budget amount, whole units — "1 500 $" (fr-CA), "$1,500"
 * (en-CA), "1.500 €" (de-DE). Deliberately NOT priceByCountry (hardcoded en-US
 * grouping); budget amounts are the user's own number and must read native.
 */
export const formatBudget = (amount: number, locale: LocaleDetails): string => {
  try {
    return new Intl.NumberFormat(locale.path, {
      style: 'currency',
      currency: getCurrencyForRegion(locale.region),
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ${getCurrencyForRegion(locale.region)}`;
  }
};

/** The number in the shopper's own terms: "$900", or "$900 for 4" for a set. */
export const formatBudgetPhrase = (
  budget: { max: number; quantity?: number },
  locale: LocaleDetails,
): string => {
  const amount = formatBudget(budget.max, locale);
  const units = budgetUnits(budget);
  return units > 1
    ? fillBudgetCopy(getBudgetUiContent(locale.languageCode).budgetForQuantity, {
        amount,
        qty: String(units),
      })
    : amount;
};

/** The chip: "≤ $900", "≤ $900 for 4", or "$800 – $1,200" for a stated range. */
export const formatBudgetLabel = (
  budget: { max: number; min?: number; quantity?: number },
  locale: LocaleDetails,
): string => {
  if (!budget.min) return `≤ ${formatBudgetPhrase(budget, locale)}`;
  const range = `${formatBudget(budget.min, locale)} – ${formatBudget(budget.max, locale)}`;
  const units = budgetUnits(budget);
  return units > 1
    ? fillBudgetCopy(getBudgetUiContent(locale.languageCode).budgetForQuantity, {
        amount: range,
        qty: String(units),
      })
    : range;
};

/** Where this locale writes its currency symbol: "$900" (en-CA) or "900 $" (fr-CA). */
export const budgetCurrencyAdornment = (locale: LocaleDetails): { symbol: string; symbolFirst: boolean } => ({
  symbol: formatBudget(0, locale).replace(/[\d\s.,]/g, ''),
  symbolFirst: !/^\d/.test(formatBudget(1, locale)),
});
